import { useEffect, useMemo, useState } from 'react';

import {
  useGetAlertThresholdsQuery,
  useGetSensorTypesQuery,
  useGetStationsQuery,
} from '@/features/admin/api/opsManagementApi';
import type { StationSensorAlertRuleRow } from '@/features/admin/types/opsExtended.types';
import { sensorTypeCatalogKeyLower } from '@/features/admin/utils/sensorTypeCatalogKey';
import type { IoTDataPoint } from '@/features/realtime/types/realtime.types';
import { applyAlertThresholdsToStations } from '@/features/realtime/utils/applyAlertThresholdsToStations';
import {
  ALERT_THRESHOLDS_PAGE_SIZE,
  fetchAllAlertThresholdRowsShared,
} from '@/features/realtime/utils/fetchAllAlertThresholdRows';
import { mapStationDtoToIoTPoint } from '@/features/realtime/utils/mapStationDtoToIoTPoint';
import { mergeStationTelemetryFromMap } from '@/features/realtime/utils/mergeStationTelemetryOverlay';
import { buildCatalogIoTTypeMap } from '@/features/realtime/utils/sensorTypeFromStationDto';
import {
  parseSensorTypeIdsFromDto,
  stationRoutingKeys,
} from '@/features/realtime/utils/stationSensorBindings';
import { useAppSelector } from '@/hooks/useAppSelector';
import { parseCssHexToRgb } from '@/utils/colorHex';
import { shouldFetchOperationalApi } from '@/utils/dataSource';
import { isApiForbiddenError } from '@/utils/isApiForbidden';

export interface UseDashboardIoTStationsOptions {
  /** Skip queries (e.g. not logged in). */
  skip?: boolean;
}

/**
 * Stations from GET /api/stations (catalog màu từ sensor-types), merge GET /api/alerts/thresholds và overlay WS.
 */
export function useDashboardIoTStations(options: UseDashboardIoTStationsOptions = {}): {
  stations: IoTDataPoint[];
  isLoading: boolean;
  isError: boolean;
  /** GET /api/stations trả 403 — người dùng không đủ quyền xem danh sách trạm. */
  stationsForbidden: boolean;
  /** Rule ngưỡng đã tải (đa trang) — dùng mô tả chuông đa kênh. */
  thresholdRows: StationSensorAlertRuleRow[];
} {
  const skip = Boolean(options.skip);
  const operational = shouldFetchOperationalApi();
  const querySkip = skip || !operational;

  const stationsQuery = useGetStationsQuery({ pageSize: 2000 }, { skip: querySkip });
  const stationsLoading = stationsQuery.isLoading;
  const stationsError = stationsQuery.isError;
  const stationsForbidden =
    !querySkip && stationsQuery.isError && isApiForbiddenError(stationsQuery.error);

  const { data: sensorTypes = [] } = useGetSensorTypesQuery(
    { page: 1, pageSize: 200 },
    { skip: querySkip },
  );

  const thPage1 = useGetAlertThresholdsQuery(
    { page: 1, pageSize: ALERT_THRESHOLDS_PAGE_SIZE },
    { skip: querySkip },
  );

  const thresholdTotal = thPage1.data?.total ?? 0;
  const needsFullThresholdFetch = !querySkip && thresholdTotal > ALERT_THRESHOLDS_PAGE_SIZE;

  const [fullThresholdRows, setFullThresholdRows] = useState<StationSensorAlertRuleRow[] | null>(
    null,
  );
  const [fullFetchOutcome, setFullFetchOutcome] = useState<{
    forKey: number;
    error: boolean;
  }>({ forKey: -1, error: false });

  const [cachedFullThresholdRows, setCachedFullThresholdRows] = useState<
    StationSensorAlertRuleRow[]
  >([]);

  const thresholdReloadKey = thPage1.fulfilledTimeStamp ?? 0;

  useEffect(() => {
    if (!needsFullThresholdFetch) return;
    let cancelled = false;
    const reloadKey = thresholdReloadKey;
    void fetchAllAlertThresholdRowsShared(reloadKey)
      .then((rows) => {
        if (!cancelled) {
          setFullThresholdRows(rows);
          if (rows.length > 0) setCachedFullThresholdRows(rows);
          setFullFetchOutcome({ forKey: reloadKey, error: false });
        }
      })
      .catch(() => {
        if (!cancelled) setFullFetchOutcome({ forKey: reloadKey, error: true });
      });
    return () => {
      cancelled = true;
    };
  }, [needsFullThresholdFetch, thresholdReloadKey]);

  const fullFetchError = fullFetchOutcome.forKey === thresholdReloadKey && fullFetchOutcome.error;

  const thresholdRows = useMemo(() => {
    const pageThresholdRows = thPage1.data?.items ?? [];
    if (!needsFullThresholdFetch) return pageThresholdRows;
    return fullThresholdRows ?? cachedFullThresholdRows;
  }, [needsFullThresholdFetch, fullThresholdRows, cachedFullThresholdRows, thPage1.data?.items]);

  const thresholdsLoading =
    thPage1.isLoading || (needsFullThresholdFetch && fullThresholdRows === null && !fullFetchError);
  const thresholdsError = thPage1.isError || fullFetchError;

  const telemetryByStationId = useAppSelector((s) => s.realtime.telemetryByStationId);

  const rgbBySensorCodeLower = useMemo(() => {
    const m = new Map<string, [number, number, number]>();
    for (const st of sensorTypes) {
      const k = sensorTypeCatalogKeyLower(st);
      if (!k) continue;
      const rgb = parseCssHexToRgb(st.color ?? undefined);
      if (rgb) m.set(k, rgb);
    }
    return m;
  }, [sensorTypes]);

  const typeMapBySensorCodeLower = useMemo(
    () => buildCatalogIoTTypeMap(sensorTypes),
    [sensorTypes],
  );

  const stations = useMemo(() => {
    const raw = stationsQuery.data ?? [];
    const enriched = raw.map((r) => {
      const pointBase = mapStationDtoToIoTPoint(r);
      const bindings = parseSensorTypeIdsFromDto(r);
      const allRks = stationRoutingKeys(bindings);
      let point: IoTDataPoint = pointBase;
      for (const rk of allRks) {
        const key = rk.trim().toLowerCase();
        const rgb = rgbBySensorCodeLower.get(key);
        if (rgb) point = { ...point, sensorTypeCatalogRgb: rgb };
      }
      const primaryCh = pointBase.sensorChannelCode?.trim().toLowerCase();
      if (primaryCh) {
        const rgb = rgbBySensorCodeLower.get(primaryCh);
        if (rgb) point = { ...point, sensorTypeCatalogRgb: rgb };
        const resolvedType = typeMapBySensorCodeLower.get(primaryCh);
        if (resolvedType) point = { ...point, type: resolvedType };
      }
      return point;
    });
    const merged = enriched.map((s) => mergeStationTelemetryFromMap(s, telemetryByStationId));
    return applyAlertThresholdsToStations(merged, thresholdRows);
  }, [
    stationsQuery.data,
    thresholdRows,
    telemetryByStationId,
    rgbBySensorCodeLower,
    typeMapBySensorCodeLower,
  ]);

  return {
    stations,
    isLoading: stationsLoading || thresholdsLoading,
    isError: stationsError || thresholdsError,
    stationsForbidden,
    thresholdRows,
  };
}
